import { colors } from "@constants/theme";
import React, { useMemo } from "react";
import Cross from "@assets/svg/CrossIcon";
import {
  StyleSheet,
  View,
  Modal,
  TouchableWithoutFeedback,
  TouchableOpacity,
  FlatList,
  ScrollView,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Button from "@components/Button";
import Text from "@components/CustomText";

interface FilterModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: () => void;
  filterData: {
    route?: { title: string; values: any[] };
    db?: { title: string; values: any[] };
    order_delay?: { title: string; values: any[] };
    call_status?: { title: string; values: any[] };
    dispatch_status?: { title: string; values: any[] };
    preferred_slot?: { title: string; values: any[] };
    is_priority?: { title: string; values: any[] };
  };
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  handleOptionPress: (item: any) => void;
  getSelectedFilters: () => any;
  clearFilters: () => void;
  metadata: {
    title: string;
    searchTermPlaceholder: string;
    submit_text: string;
    clear_text: string;
  };
}

const FilterModalContent: React.FC<FilterModalProps> = ({
  onClose,
  onSave,
  filterData,
  selectedCategory,
  setSelectedCategory,
  handleOptionPress,
  getSelectedFilters,
  clearFilters,
  metadata: { title, submit_text, clear_text },
}) => {
  // Insets from the SafeAreaProvider nested inside the Modal (wrapper below),
  // so this reflects the modal's own translucent window. The root provider
  // reports bottom: 0 while a navigationBarTranslucent modal is open, clipping
  // the action buttons behind the soft-nav bar.
  const insets = useSafeAreaInsets();
  const categoriesV2 = useMemo(
    () =>
      Object.keys(filterData || {}).map((key) => ({
        key,
        label: filterData[key as keyof typeof filterData]?.title,
      })),
    [filterData],
  );

  const getCurrentFiltersV2 = () => {
    return filterData?.[selectedCategory as keyof typeof filterData];
  };
  const renderCategory = ({
    item,
  }: {
    item: { key: string; label: string };
  }) => (
    <TouchableOpacity
      style={[
        styles.categoryButton,
        selectedCategory === item.key && styles.activeCategory,
      ]}
      onPress={() => setSelectedCategory(item.key)}
      testID={`${item.key}+${item.label}`}
    >
      <Text
        variant="body_text_3"
        style={selectedCategory === item.key ? styles.activeCategoryText : {}}
      >
        {item.label}
      </Text>
    </TouchableOpacity>
  );

  return (
    <>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={[styles.contentContainer, { paddingBottom: insets.bottom }]}>
        {/* The touchable wraps the circle View, not the bare SVG: it hands its
            press props to its direct child, and the SVG component drops them
            on web, which left the ✕ dead there. */}
        <TouchableWithoutFeedback onPress={onClose}>
          <View style={styles.modalCross}>
            <Cross width={16} height={16} />
          </View>
        </TouchableWithoutFeedback>
        <View style={styles.headingContainer}>
          <Text variant="title_20">{title}</Text>
        </View>
        <View style={styles.scrollContainer}>
          <FlatList
            data={categoriesV2}
            renderItem={renderCategory}
            keyExtractor={(item) => item.key}
            style={styles.categoryList}
          />
          <FilterPills
            data={getCurrentFiltersV2()}
            selectedFilter={getSelectedFilters()}
            setSelectedFilter={handleOptionPress}
          />
        </View>
        <View style={styles.spacer} />
        <View
          style={{
            paddingHorizontal: 10,
            flexDirection: "row",
            paddingVertical: 30,
            justifyContent: "space-evenly",
            borderTopColor: colors.black02,
            borderTopWidth: 0.5,
            bottom: 0,
            position: "absolute",
            backgroundColor: colors.white,
            width: "100%",
          }}
        >
          <View style={styles.buttonWrapper}>
            <Button
              borderRadius={8}
              btnText={clear_text}
              buttonWidth="100%"
              height={42}
              shouldBoldText
              buttonFontType="head3"
              buttonColor={colors.white}
              textColor={colors.black}
              borderColor={colors.black02}
              onPress={() => clearFilters()}
            />
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              borderRadius={8}
              btnText={submit_text}
              buttonWidth="100%"
              height={42}
              shouldBoldText
              buttonFontType="head3"
              buttonColor={colors.plain_green}
              textColor={colors.white}
              borderColor={colors.plain_green}
              onPress={() => onSave()}
            />
          </View>
        </View>
      </View>
    </>
  );
};

const FilterModal: React.FC<FilterModalProps> = (props) => {
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={props.visible}
      onRequestClose={props.onClose}
      statusBarTranslucent
      navigationBarTranslucent
    >
      {/* Nested provider measures the modal's own translucent window so
          useSafeAreaInsets() reports the real nav-bar inset inside the Modal. */}
      <SafeAreaProvider>
        <FilterModalContent {...props} />
      </SafeAreaProvider>
    </Modal>
  );
};

interface FilterPillsProps {
  data?: {
    title: string;
    values: any[];
  };
  selectedFilter: any;
  setSelectedFilter: (filter: any) => void;
}

const FilterPills: React.FC<FilterPillsProps> = ({
  data,
  selectedFilter,
  setSelectedFilter,
}) => {
  return (
    <ScrollView
      style={{ padding: 10, paddingVertical: 12, width: "70%" }}
      showsVerticalScrollIndicator={false}
    >
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        {data?.values?.map((item) => (
          <TouchableOpacity
            testID="mt-filter-pill"
            key={item?.created_at || item?.key}
            style={[
              styles.pill,
              {
                borderColor:
                  JSON.stringify(selectedFilter) === JSON.stringify(item)
                    ? colors.plain_green
                    : "rgba(0, 0, 0, 0.2)",
                backgroundColor:
                  JSON.stringify(selectedFilter) === JSON.stringify(item)
                    ? colors.plain_green
                    : colors.white,
              },
            ]}
            onPress={() => setSelectedFilter(item)}
          >
            <Text
              variant="body_text_3"
              numberOfLines={1}
              style={{
                color:
                  JSON.stringify(selectedFilter) === JSON.stringify(item)
                    ? colors.white
                    : colors.black,
              }}
            >
              {item?.route_name || item?.db_name || item?.value}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={{ height: 200 }} />
    </ScrollView>
  );
};

export default FilterModal;

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
    minHeight: "60%",
    maxHeight: "70%",
  },

  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 20,
    alignItems: "center",
    borderBottomColor: colors.black02,
    borderBottomWidth: 0.5,
  },

  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: "#fff",
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  pill: {
    flexWrap: "wrap",
    marginTop: 8,
    marginRight: 8,
    padding: 4,
    paddingHorizontal: 18,
    borderRadius: 24,
    overflow: "hidden",
    borderWidth: 1,
    marginBottom: 8,
  },
  buttonWrapper: {
    flex: 0.45,
  },
  scrollContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  categoryList: {
    width: "35%",
    backgroundColor: "rgba(245, 245, 245, 1)",
    paddingBottom: 50,
  },
  categoryButton: {
    paddingVertical: 20,
    paddingHorizontal: 10,
    borderBottomColor: colors.white,
    borderBottomWidth: 1,
  },

  activeCategory: {
    backgroundColor: colors.white,
    borderLeftColor: colors.color_D9D9D9,
    borderLeftWidth: 6,
  },

  activeCategoryText: {
    fontWeight: "bold",
    color: colors.black,
  },
  spacer: {
    height: 60,
  },
});
